<template>
  <b-container fluid>
    <!-- Search row -->
    <b-row class='my-2'>
      <!-- Search orderNo. -->
      <b-col style="min-width:250px;max-width: 300px;">      
        <b-input-group size="sm" class="m-1">
          <b-input-group-prepend is-text>
            {{ $t("globals.invoice_no") }}
          </b-input-group-prepend>
          <b-form-input
            v-model="searchQuery.invoiceNo"
            @keyup.enter="searchBillingInvoicesLikeInvoiceNo"
            :disabled="getAllBillingInvoicesLoading"
          />
          <b-input-group-append>
            <b-btn
              variant="info"
              :disabled="getAllBillingInvoicesLoading"
              @click="searchBillingInvoicesLikeInvoiceNo"
            >
              <v-icon name="search"></v-icon>
            </b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-col>
      <!-- Search Customer -->
      <b-col style="min-width:250px;max-width: 300px;">
        <b-input-group size="sm" class="m-1">
          <b-input-group-prepend is-text>
            {{ $t("globals.customer") }}
          </b-input-group-prepend>
          <b-form-input
            v-model="searchQuery.customerName"
            @keyup.enter="searchBillingInvoicesLikeCustomerName"
            :disabled="getAllBillingInvoicesLoading"
          />
          <b-input-group-append>
            <b-btn
              variant="info"
              :disabled="getAllBillingInvoicesLoading"
              @click="searchBillingInvoicesLikeCustomerName"
            >
              <v-icon name="search"></v-icon>
            </b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-col>
      <!-- Sent at from -->
      <b-col style="min-width:250px;max-width: 300px;">
        <b-form-datepicker
          class="m-1" size='sm'
          :placeholder="'Sent from'"
          v-model="searchQuery.sentFrom"
          :disabled="getAllBillingInvoicesLoading"
        ></b-form-datepicker>
      </b-col>
      <!-- Sent at to -->
      <b-col style="min-width:250px;max-width: 300px;">
        <b-form-datepicker
          class="m-1" size='sm'
          :placeholder="'Sent to'"
          v-model="searchQuery.sentTo"
          :min="new Date(searchQuery.sentFrom)"
          :disabled="getAllBillingInvoicesLoading"
        ></b-form-datepicker>
      </b-col>
      <!-- Clear/Reload -->
      <b-col style="min-width: 130px">
        <b-btn
          variant="danger"
          size="sm"
          class="m-1 float-right"
          @click="resetReload()"
        >
          {{ $t("globals.clear") + " / " + $t("globals.reload") }}
        </b-btn>
      </b-col>
    </b-row>
    <!-- Table Row -->
    <b-row>
      <b-col>
        <b-table
          small hover
          primary-key="id"
          :per-page="0"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :items="getAllBillingInvoices"
          :fields="fields"
          @sort-changed="sortingChanged"
          @row-clicked='rowClicked'
        >
          <!-- Customer -->
          <template v-slot:cell(customer)="row">
            <div v-if="row.item.customer">
              <router-link
                style="color: #17a2b8"
                :to="{ name: 'customer', params: { id: row.item.customer.id } }"
                target="_blank"
              >
                <i>{{ row.item.customer.company_name }}</i>
              </router-link>
            </div>
          </template>
          <!-- Invoice no -->
          <template v-slot:cell(invoice_no)="row">
            <a href="#" style='color: #17a2b8' @click="openPdf(row.item.id)"><i>{{ row.item.invoice_no }}</i></a>
          </template>
          <!-- Order -->
          <template v-slot:cell(order)="row">
            <div v-if="row.item.order">
              <router-link
                style="color: #17a2b8"
                :to="{ name: 'order', params: { id: row.item.order.id } }"
                target="_blank"
              >
                <i>{{ row.item.order.order_no }}</i>
              </router-link>
            </div>
          </template>
          <!-- Internal Notes -->
          <template v-slot:cell(internal_notes)="row">
            <span
              v-if="row.item.internal_notes !== null"
              v-b-tooltip.hover
              :title="row.item.internal_notes"
            >
              {{
                row.item.internal_notes.length > 15
                  ? row.item.internal_notes.substring(0, 15) + "..."
                  : row.item.internal_notes
              }}
            </span>
          </template>
          <!-- Total price -->
          <template v-slot:cell(total_price)="row">
            {{ currencyFormat(row.item.total_price, row.item.currency) }}
          </template>
          <!-- Total price ex vat -->
          <template v-slot:cell(total_price_ex_vat)="row">
            {{ currencyFormat(row.item.total_price_ex_vat, row.item.currency) }}
          </template>
          <!-- Created At -->
          <template v-slot:cell(created_at)="row">
            <div v-b-tooltip.hover :title="row.item.created_at">
              {{ row.item.created_at.substring(0, 10) }}
            </div>
          </template>
          <!-- Paid -->
          <template v-slot:cell(paid_at)="row">
            <div
              v-if="row.item.paid_at !== null"
              v-b-tooltip.hover :title="row.item.paid_at"
            >
              <b-badge variant='success'>
                {{ $t('globals.paid') }}
              </b-badge>
            </div>
          </template>
          <!-- Credit Invoice No and is_credit -->
          <template v-slot:cell(credit_invoice_no)="row">
            {{ row.item.credit_invoice_no }}
            <b-badge v-if="row.item.is_credit_invoice" variant='danger'>
              {{ $t('globals.credit') }}
            </b-badge>
          </template>
          <!-- Sent At -->   
          <template v-slot:cell(sent_at)="row">
            <div 
              v-if='row.item.sent_at !== null'
              v-b-tooltip.hover :title="row.item.sent_at + '\n' + row.item.sent_to"
            >
              <b-badge>
                {{ row.item.sent_at.substring(0, 10) }}
              </b-badge>
            </div>
          </template>
          <!-- Re Sent At -->   
          <template v-slot:cell(re_sent_at)="row">
            <div 
              v-if='row.item.re_sent_at !== null'
              v-b-tooltip.hover :title="row.item.re_sent_at"
            >
              <b-badge>
                {{ row.item.re_sent_at.substring(0, 10) }}
              </b-badge>
            </div>
          </template>
          <!-- Due date -->
          <template v-slot:cell(due_date)="row">
            <div 
              v-if='row.item.due_date !== null'
              v-b-tooltip.hover :title="row.item.due_date"
            >
              {{ row.item.due_date.substring(0, 10) }}
            </div>
          </template>
        </b-table>
      </b-col>
    </b-row>
    <!-- Pagination -->
    <simple-pagination-row
      :loading="getAllBillingInvoicesLoading"
      :offset="offset"
      :fetchLess="fetchLess"
      :fetchMore="fetchMore"
    />
  </b-container>
</template>

<script>
import { currencyFormat } from "@/helpers/price";
import { mapGetters, mapActions } from "vuex";

export default {
  components: {
    "simple-pagination-row": () =>
      import("@/components/common/SimplePaginationRow"),
  },
  data() {
    return {
      showNoteModal: false,
      notes: "",
      invoice_notes_id: null,
      offset: 0,
      limit: 20,
      sortBy: "created_at",
      sortDesc: true,
      searchQuery: {
        invoiceNo: null,
        customerName: null,
        sentFrom: null,
        sentTo: null,
      },
      fields: [
        {
          key: "id",
          sortable: true,
        },
        {
          key: "invoice_no",
          label: this.$t("globals.invoice_no"),
          sortable: true,
        },
        {
          key: "credit_invoice_no",
          label: this.$t("globals.credit_invoice"),
          sortable: true,
        },
        {
          key: "sent_at",
          label: this.$t("globals.sent_at"),
          sortable: true,
        },
        {
          key: "re_sent_at",
          label: this.$t("globals.re_sent_at"),
          sortable: true,
        },
        {
          key: "paid_at",
          label: this.$t("globals.paid"),
          sortable: true,
        },
        {
          key: "due_date",
          label: this.$t("globals.due_date"),
          sortable: true,
        },
        {
          key: "total_price",
          label: this.$t("globals.total"),
          class: "text-right",
          sortable: true,
        },
        {
          key: "total_price_ex_vat",
          label: this.$t("globals.total_price_no_vat"),
          class: "text-right",
          sortable: true,
        },
        {
          key: "customer",
          label: this.$t("globals.customer"),
          sortable: true,
        },
        {
          key: "order",
          label: this.$t("globals.order_no"),
          sortable: true,
        },
        {
          key: "internal_notes",
          label: this.$t("globals.internal_notes"),
          sortable: true,
        },
        {
          key: "created_at",
          label: this.$t("globals.created_at"),
          class: "text-right",
          sortable: true,
        },
      ],
    };
  },
  methods: {
    ...mapActions("axios_mod", ["simpleMutation"]),
    ...mapActions("invoices", [
      "fetchBillingInvoicesPagination",
      "fetchBillingInvoicesLikeInvoiceNo",
      "fetchBillingInvoicesBySentAtRange",
      "fetchBillingInvoicesLikeCustomerName"
    ]),
    currencyFormat,
    rowClicked(item) {
      this.$router.push({ name: "invoice", params: { id: item.id } });
    },
    goToOrder(id) {
      this.$router.push({ name: "order", params: { id: id } });
    },
    toCustomer(id) {
      this.$router.push({ name: "customer", params: { id: id } });
    },
    openPdf(id) {
      window.open(
        `${this.getBackendUrl}/pdf_invoices/${id}.pdf?tmpl=0&locale=${this.getLocale}`,
        "_blank"
      );
    },
    fetchMore() {
      this.offset += 20;
      // this.selectFetch();
      this.fetch();
    },
    fetchLess() {
      if (this.offset >= 20) {
        this.offset -= 20;
        // this.selectFetch();
        this.fetch();
      }
    },
    resetOffsetSort() {
      this.offset = 0;
      this.sortBy = "created_at";
      this.sortDesc = true;
    },
    resetQuery() {
      this.searchQuery.invoiceNo = null;
      this.searchQuery.customerName = null;
      this.searchQuery.sentFrom = null;
      this.searchQuery.sentTo = null;
    },
    fetch() {
      let order_by = this.sortBy;
      // Customer exception
      if (order_by === "customer") {
        order_by = "customer_id";
      } else if (order_by === "order") {
        order_by = "order_id";
      }

      if (this.sortDesc) {
        order_by += " DESC";
      } else {
        order_by += " ASC";
      }

      this.fetchBillingInvoicesPagination({
        this_ref: this,
        limit: this.limit,
        offset: this.offset,
        order: order_by,
      });
    },
    searchBillingInvoicesLikeInvoiceNo() {
      if (this.searchQuery.invoiceNo != null && this.searchQuery.invoiceNo.length > 2) {
        this.searchQuery.customerName = null;
        this.searchQuery.sentFrom = null;
        this.searchQuery.sentTo = null;

        this.fetchBillingInvoicesLikeInvoiceNo({
          this_ref: this,
          invoice_no: this.searchQuery.invoiceNo
        });
      }
    },
    searchBillingInvoicesLikeCustomerName() {
      if (this.searchQuery.customerName != null && this.searchQuery.customerName.length > 2) {
        this.searchQuery.invoiceNo = null;
        this.searchQuery.sentFrom = null;
        this.searchQuery.sentTo = null;

        this.fetchBillingInvoicesLikeCustomerName({
          this_ref: this,
          customer_name: this.searchQuery.customerName
        });
      }
    },
    searchBillingInvoicesBySentAtRange() {
      this.searchQuery.invoiceNo = null;
      this.searchQuery.customerName = null;

      this.fetchBillingInvoicesBySentAtRange({
        this_ref: this,
        sent_from: this.searchQuery.sentFrom,
        sent_to: this.searchQuery.sentTo
      });
    },
    sortingChanged(ctx) {
      // Field key for sorting by (or null for no sorting)
      this.sortBy = ctx.sortBy || "created_at";
      // true if sorting descending, false otherwise
      this.sortDesc = ctx.sortDesc;

      this.fetch();
    },
    resetReload() {
      this.resetOffsetSort();
      this.resetQuery();
      this.fetch();
    },
  },
  computed: {
    ...mapGetters("invoices", [
      "getAllBillingInvoices",
      "getAllBillingInvoicesLoading",
    ]),
    ...mapGetters(["getBackendUrl", "getLocale", "getUserBrandPermissions"]),
  },
  watch: {
    "searchQuery.sentFrom": function(val) {
      if (val) {
        this.searchBillingInvoicesBySentAtRange();
      }
    },
    "searchQuery.sentTo": function(val) {
      if (val) {
        this.searchBillingInvoicesBySentAtRange();
      }
    },
  },
  mounted() {
    this.fetch();
  },
};
</script>
